import { EditFilled } from '@ant-design/icons';
import { StaticInput, useField } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { ModalButton, TableData } from '@framework/ui';
import { Space, Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { RichTextAdvancedRender } from '../../../../../../../inputs';
import { ConfigFormItem } from '../../../../../../../ui/form/ConfigFormItem';
import { IAsyncService } from '../../../../../../expressions/repository/async/context/IAsyncService';
import { IWidgetParsedProps } from '../../../common/util/getWidgetProps';
import { IAsyncSelectWidget } from '../../IAsyncSelectWidget';
import { getSelectedValueLabel } from '../label/getSelectedValueLabel';
import { useAsyncServiceTableColumns } from './useAsyncServiceTableColumns';

const FullWidthCompact = styled(Space.Compact)`
  width: 100%;
`;

export interface IAsyncServiceTableInputProps {
  widgetOptions: IWidgetParsedProps;
  data: any[] | null;
  isLoading: boolean;
  error: any;
  widget: IAsyncSelectWidget;
  objectFieldName: string;
  service: IAsyncService | null;
  setSearchQuery: (value: string | null) => void;
}

/**
 * Input async che permette la selezione attraverso una modal con una tabella.
 */
export function AsyncServiceTableInput(props: IAsyncServiceTableInputProps) {
  const {
    widgetOptions,
    data,
    isLoading,
    error,
    widget,
    objectFieldName,
    service,
  } = props;

  const field = useField(widgetOptions.name);
  const { field: objectField } = useController({ name: objectFieldName });
  const value = useWatch({ name: widgetOptions.name });

  const labelValue = useMemo(() => {
    const values = data?.find(d => d[widget.valueField] === value);
    if (!values) return '';
    return getSelectedValueLabel(widget, values);
  }, [data, value, widget]);

  const dataSource = useMemo(() => data ?? [], [data]);

  const { t } = useTranslation();

  const columns = useAsyncServiceTableColumns(widget);

  const [selected, setSelected] = useState<Record<string, any> | null>(value);

  const handleSelect = useCallback(() => {
    field.setValue(selected?.[widget.valueField] ?? null, {
      // In questo modo resetta l'errore se presente (e.g. required)
      shouldValidate: true,
    });
    objectField.onChange(selected ?? null);
  }, [field, objectField, selected, widget.valueField]);

  return (
    // TODO Aggiungere pulsante per reset del valore
    <ConfigFormItem
      name={widgetOptions.name}
      label={<RichTextAdvancedRender inline text={widgetOptions.label} />}
      help={<RichTextAdvancedRender inline text={widgetOptions.helpText} />}
      layout={{ hideVisualLabel: widgetOptions.hideLabel }}
      readonly={widgetOptions.readonly}
    >
      <FullWidthCompact>
        <StaticInput
          value={labelValue}
          placeholder={widgetOptions.placeholder}
          loading={isLoading}
        />
        <ModalButton
          buttonChildren={t('widgets.async-select.select-text', 'Scegli')}
          type="primary"
          size="large"
          modalProps={{
            okText: t('widgets.select-widget.select-text', 'Scegli'),
          }}
          onOk={handleSelect}
          style={{ width: '130px' }}
          icon={<EditFilled />}
          fieldName={widgetOptions.name}
        >
          <TableData
            dataSource={dataSource}
            size="small"
            loading={isLoading}
            pagination={false}
            columns={columns}
            darkTitleBar={false}
            rowKey={widget.valueField}
            rowSelection={{
              type: 'radio',
              onChange: (_, selectedRows) => {
                setSelected(selectedRows[0]);
              },
              selectedRowKeys:
                selected?.[widget.valueField] != null
                  ? [selected[widget.valueField]]
                  : undefined,
            }}
          />
        </ModalButton>
      </FullWidthCompact>
      {error && (
        <Typography.Text type="danger">
          {t(
            'widgets.async-select-widget-component.service-not-responding',
            'Impossibile recuperare i dati dal servizio {{service}}. Riprovare più tardi.',
            { service: service?.name ? `"${service.name}"` : '' },
          )}
        </Typography.Text>
      )}
    </ConfigFormItem>
  );
}
